import React, { useEffect, useRef } from "react";
import "./ExperienceTimeline.css";
import { Fade } from "react-reveal";

function ExperienceTimeline({ sections, theme }) {
  // Create refs for each timeline item for scroll animations
  const timelineRefs = useRef([]);

  // Set up refs array based on total number of experience items
  useEffect(() => {
    const totalItems = sections.reduce(
      (count, section) => count + section.experiences.length,
      0,
    );
    timelineRefs.current = Array(totalItems)
      .fill()
      .map(() => React.createRef());
  }, [sections]);

  // Check if an element is in viewport
  const isInViewport = (element) => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };

  // Handle scroll animation
  useEffect(() => {
    const handleScroll = () => {
      timelineRefs.current.forEach((ref) => {
        if (ref.current && isInViewport(ref.current)) {
          ref.current.classList.add("active");
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    // Initial check
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Counter for tracking total timeline items across sections
  let timelineItemCount = 0;

  return (
    <div className="timeline-container">
      {sections.map((section, sectionIndex) => (
        <div key={section.title} className="timeline-section">
          <Fade bottom duration={1000} distance="20px">
            <div
              className="timeline-section-header"
              style={{ color: theme.text, borderColor: theme.headerColor }}
            >
              <h2>{section.title}</h2>
            </div>
          </Fade>

          <div className="timeline">
            {section.experiences.map((experience, index) => {
              // Assign a ref to this timeline item
              const currentRef = timelineRefs.current[timelineItemCount];
              timelineItemCount++;

              return (
                <Fade
                  key={index}
                  bottom
                  duration={1000}
                  distance="20px"
                  delay={200 * index}
                >
                  <div className="timeline-item" ref={currentRef}>
                    <div
                      className="timeline-item-content"
                      style={{
                        backgroundColor: theme.body,
                        boxShadow: `0 10px 40px ${
                          theme.imageDark === "#FFFFFF"
                            ? "rgba(0, 0, 0, 0.1)"
                            : "rgba(0, 0, 0, 0.25)"
                        }`,
                      }}
                    >
                      <div className="timeline-item-header">
                        <div className="logo-container">
                          <img
                            src={require(
                              `../../assets/images/${experience.logo_path}`,
                            )}
                            alt={experience.company}
                            className="company-logo"
                          />
                        </div>
                        <div className="timeline-item-title">
                          <h3 style={{ color: theme.text }}>
                            {experience.title}
                          </h3>
                          <div className="timeline-item-meta">
                            <a
                              href={experience.company_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {experience.company}
                            </a>
                            <div className="timeline-item-duration">
                              <p style={{ color: theme.secondaryText }}>
                                {experience.duration}
                              </p>
                              <p style={{ color: theme.secondaryText }}>
                                {experience.location}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="timeline-item-description"
                        style={{ color: theme.text }}
                      >
                        {experience.description.split("\n").map((item, key) => (
                          <p key={key}>{item.trim()}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                </Fade>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default ExperienceTimeline;
